import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  ContentVideo,
  loadVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  extractYouTubeVideoId,
  buildYouTubeThumbnailUrl,
} from '../helpers/videoHelpers';

interface VideoContextValue {
  videosByContentId: Record<string, ContentVideo[]>;
  isLoading: boolean;
  loadContentVideos: (contentId: string) => Promise<ContentVideo[]>;
  addVideoToContent: (
    contentId: string,
    data: { title: string; description?: string | null; youtube_url: string; is_published?: boolean }
  ) => Promise<ContentVideo | null>;
  updateVideoItem: (id: string, updates: Partial<ContentVideo>) => Promise<ContentVideo | null>;
  deleteVideoItem: (id: string) => Promise<boolean>;
}

const VideoContext = createContext<VideoContextValue | undefined>(undefined);

export const VideoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [videosByContentId, setVideosByContentId] = useState<Record<string, ContentVideo[]>>({});
  const [isLoading, setIsLoading] = useState(false);

  const loadContentVideos = useCallback(async (contentId: string) => {
    setIsLoading(true);
    try {
      const list = await loadVideos(contentId);
      setVideosByContentId(prev => ({ ...prev, [contentId]: list }));
      return list;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addVideoToContent: VideoContextValue['addVideoToContent'] = useCallback(async (contentId, data) => {
    const videoId = extractYouTubeVideoId(data.youtube_url || '');
    if (!videoId) return null;
    const current = videosByContentId[contentId] || [];
    const order = current.length;

    const created = await createVideo({
      content_id: contentId,
      title: data.title,
      description: data.description ?? null,
      youtube_url: data.youtube_url,
      youtube_video_id: videoId,
      thumbnail_url: buildYouTubeThumbnailUrl(videoId),
      order_index: order,
      is_published: true,
    });

    if (created) {
      setVideosByContentId(prev => ({ ...prev, [contentId]: [...current, created] }));
    }
    return created;
  }, [videosByContentId]);

  const updateVideoItem = useCallback(async (id: string, updates: Partial<ContentVideo>) => {
    const updated = await updateVideo(id, updates);
    if (!updated) return null;
    setVideosByContentId(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(contentId => {
        next[contentId] = next[contentId].map(v => (v.id === id ? updated : v));
      });
      return next;
    });
    return updated;
  }, []);

  const deleteVideoItem = useCallback(async (id: string) => {
    const ok = await deleteVideo(id);
    if (ok) {
      setVideosByContentId(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(contentId => {
          next[contentId] = next[contentId].filter(v => v.id !== id);
        });
        return next;
      });
    }
    return ok;
  }, []);

  const value = useMemo<VideoContextValue>(() => ({
    videosByContentId,
    isLoading,
    loadContentVideos,
    addVideoToContent,
    updateVideoItem,
    deleteVideoItem,
  }), [videosByContentId, isLoading, loadContentVideos, addVideoToContent, updateVideoItem, deleteVideoItem]);

  return <VideoContext.Provider value={value}>{children}</VideoContext.Provider>;
};

export function useVideos() {
  const ctx = useContext(VideoContext);
  if (!ctx) throw new Error('useVideos deve ser usado dentro de VideoProvider');
  return ctx;
}


